import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import { BASE_URL } from "./Url";
import axios from "axios";
import ImageZoom from "./ImageZoom";

const Checkbox = (props) => (
  <input style={{ transform: "scale(1.3)" }} type="checkbox" {...props} />
);

// Builds the "Product [SKU] - Variant [[SKU]]" label, omitting a bracket
// segment entirely when that SKU is missing instead of leaving an empty "[]"/
// "[[]]" behind.
export const buildVariantLabel = (
  productName,
  productSku,
  variantName,
  variantSku
) => {
  const productPart = productSku ? `${productName} [${productSku}]` : productName;
  const variantPart = variantSku ? `${variantName} [[${variantSku}]]` : variantName;
  return `${productPart} - ${variantPart}`;
};

// Same product_list_for_invoice_pagination/ endpoint used by SelectPagginationEdit /
// SelectOpenWithPaggination, but expanded to one option per variant (like the regular
// invoice's VariantSelectWithPagination) instead of one option per product. Each option
// keeps the product-level price/discount/stock alongside the variant so existing
// getBasePlusVariant()-style (base price + variant price) calculations keep working.
const buildVariantOptions = (results) => {
  const options = [];
  results.forEach((product) => {
    (product.variants || []).forEach((variant) => {
      const uniqueKey = `${product.sku || product.id}_${variant.name}_${
        variant.sku || variant.id
      }`;
      options.push({
        label: buildVariantLabel(
          product.name,
          product.sku,
          variant.name,
          variant.sku
        ),
        value: uniqueKey,
        uniqueKey,
        product_id: product.id,
        product_name: product.name,
        product_sku: product.sku,
        product_price: product.price,
        product_discount: product.discount,
        product_stock: product.stock,
        product_thumb: product.thumb_url,
        variant_id: variant.id,
        variant_name: variant.name,
        variant_sku: variant.sku,
        variant_price: variant.price,
        variant_stock: variant.stock,
        image: product?.thumb_resized_url
          ? product.thumb_resized_url
          : `${BASE_URL}media/default/lazy_placeholder.jpg`,
      });
    });
  });
  return options;
};

const VariantSelectPaggination = ({
  handleMultiInputChange,
  inputProduct,
  ProductsList,
  defaultValue,
  setProductsList,
  inputQuery,
}) => {
  const ref = useRef(null);
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [next, setNext] = useState(null);

  const toggleMenuIsOpen = () => {
    setMenuIsOpen((value) => !value);
    const selectEl = ref.current;
    if (!selectEl) return;
    if (menuIsOpen) selectEl.blur();
    else selectEl.focus();
  };

  const getProductList = () => {
    if (inputQuery?.length > 1) {
      const url = inputQuery
        ? `${BASE_URL}api/v1/inventory/inventory/product_list_for_invoice_pagination/?query=${inputQuery}&limit=20`
        : `${BASE_URL}api/v1/inventory/inventory/product_list_for_invoice_pagination/`;

      setLoading(true);
      axios
        .get(url)
        .then((res) => {
          const { results } = res.data.data;
          setProductsList(buildVariantOptions(results));
          setNext(res.data.data.next);
          setMenuIsOpen(true);
          setLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching product list:", err);
          setLoading(false);
        });
    }
  };

  useEffect(() => {
    let timeoutId;
    const delayedFetch = () => {
      timeoutId = setTimeout(getProductList, 1000);
    };
    delayedFetch();
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputQuery]);

  const handleLoadMore = () => {
    if (!next) return;
    setLoading(true);
    axios
      .get(next)
      .then((res) => {
        const { results } = res.data.data;
        setProductsList((prevList) => [
          ...prevList,
          ...buildVariantOptions(results),
        ]);
        setNext(res.data.data.next);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching product list:", err);
        setLoading(false);
      });
  };

  useEffect(() => {
    const styleElement = document.createElement("style");
    styleElement.textContent = `
      .custom-option { display: flex; align-items: center; min-height: 35px; padding: 4px 0; }
      .custom-option span { cursor: pointer; font-size: 14px; word-break: break-word; white-space: normal; line-height: 1.2; padding-right: 8px; }
      @media (max-width: 768px) { .custom-option span { font-size: 12px; padding-left: 10px; } }
    `;
    document.head.appendChild(styleElement);
    return () => document.head.removeChild(styleElement);
  }, []);

  const CustomOption = ({ innerRef, innerProps, data }) => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    useEffect(() => {
      const handleResize = () => setIsMobile(window.innerWidth <= 768);
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, []);
    return (
      <div ref={innerRef} {...innerProps} className="custom-option">
        <ImageZoom
          src={data.product_thumb || data.image}
          style={{ display: "flex", alignItems: "center" }}
        >
          {!isMobile && (
            <img
              src={data.image}
              alt=""
              style={{ width: "35px", marginRight: "10px", marginLeft: "10px" }}
            />
          )}
          <span
            style={{
              cursor: "pointer",
              fontSize: isMobile ? "12px" : "16px",
              wordBreak: "break-word",
              whiteSpace: "normal",
              lineHeight: "1.4",
              paddingRight: "8px",
              paddingLeft: isMobile ? "10px" : "0",
            }}
          >
            {data.label}
          </span>
        </ImageZoom>
      </div>
    );
  };

  return (
    <div className="main-select">
      <div className="my-2 d-flex justify-content-between align-items-center gap-3">
        <div>
          <Checkbox
            checked={menuIsOpen}
            onChange={toggleMenuIsOpen}
            id="cypress-single__clearable-checkbox"
          />
          <span
            style={{
              marginLeft: "8px",
              fontSize: window.innerWidth <= 768 ? "12px" : "14px",
            }}
          >
            {menuIsOpen ? "Close" : "Open"}
          </span>
        </div>
        <div>
          {next !== null && (
            <button
              className="btn btn-danger"
              onClick={handleLoadMore}
              disabled={loading}
              style={{
                fontSize: window.innerWidth <= 768 ? "12px" : "14px",
                padding: window.innerWidth <= 768 ? "4px 8px" : "6px 12px",
              }}
            >
              {loading ? "Loading..." : "Load More"}
            </button>
          )}
        </div>
      </div>

      <div className="custom-menu">
        <Select
          components={{ Option: CustomOption }}
          ref={ref}
          menuIsOpen={menuIsOpen}
          isMulti
          placeholder="---Select---"
          isClearable
          onChange={handleMultiInputChange}
          onInputChange={inputProduct}
          options={ProductsList}
          defaultValue={defaultValue}
          styles={{
            control: (baseStyles, state) => ({
              ...baseStyles,
              borderColor: state.isFocused ? "blue" : "black",
              boxShadow: state.isFocused
                ? "0 0 0px 0.5px rgba(255, 0, 0, 1)"
                : "none",
              "&:hover": { borderColor: "blue" },
              maxHeight: "120px",
              overflowY: "auto",
            }),
            multiValueContainer: (baseStyles) => ({
              ...baseStyles,
              overflowY: "auto",
              maxHeight: "120px",
              display: "flex",
              flexWrap: "wrap",
            }),
          }}
        />
      </div>
    </div>
  );
};

export default VariantSelectPaggination;
